import { useEffect, useRef } from 'react';
import { FX_BUTTON_SELECTOR, ONO_POOL_SIZE, ONO_ANGLE_RANGE_DEG } from './fxConstants';
import { createPickRandom } from './onomatopoeia';
import { playClick } from './sfx';

/**
 * Global pointerdown listener that spawns short-lived onomatopoeia spans
 * at the click point. Uses an object pool of pre-created span nodes to
 * avoid GC churn (max ONO_POOL_SIZE simultaneous spawns).
 */
export default function OnomatopoeiaLayer() {
  const rootRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    const root = rootRef.current;
    if (!root) return;

    // Pre-create the pool.
    const pool: HTMLSpanElement[] = [];
    for (let i = 0; i < ONO_POOL_SIZE; i += 1) {
      const span = document.createElement('span');
      span.className = 'fx-ono';
      span.setAttribute('aria-hidden', 'true');
      root.appendChild(span);
      pool.push(span);
    }

    const pickWord = createPickRandom();
    const free = new Set(pool);

    const acquire = (): HTMLSpanElement | null => {
      const it = free.values().next();
      if (it.done) return null;
      free.delete(it.value);
      return it.value;
    };

    const release = (span: HTMLSpanElement) => {
      span.classList.remove('ono-pop');
      span.textContent = '';
      free.add(span);
    };

    const onDown = (e: PointerEvent) => {
      if (e.button > 0) return;
      const target = e.target as Element | null;
      if (!target?.closest?.(FX_BUTTON_SELECTOR)) return;
      playClick();
      const span = acquire();
      if (!span) return;
      const angle = (Math.random() * 2 - 1) * ONO_ANGLE_RANGE_DEG;
      span.style.left = `${e.clientX}px`;
      span.style.top = `${e.clientY}px`;
      span.style.setProperty('--r', `${angle}deg`);
      span.textContent = pickWord();
      // Force reflow so re-adding the class restarts the animation.
      void span.offsetWidth;
      span.classList.add('ono-pop');
      const onEnd = () => { span.removeEventListener('animationend', onEnd); release(span); };
      span.addEventListener('animationend', onEnd);
    };

    document.addEventListener('pointerdown', onDown, { passive: true, capture: true });
    return () => {
      document.removeEventListener('pointerdown', onDown, true);
      pool.forEach((s) => s.remove());
    };
  }, []);

  return <div ref={rootRef} className="fx-ono-root" aria-hidden="true" />;
}
